import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { OrdersFilterParams, OrdersListResponse, OrderType, UpdateOrderStatusParams } from '@/types/order'
import { BulkDeleteResponse, BulkIdParams } from '@/types/general'
import { getErrorMessage } from '@/lib/utils'
import { bulkDeleteOrders, deleteOrder, fetchOrder, fetchOrders, updateOrderStatus } from '@/lib/api'

type OrderStateType = {
  isLoading: boolean
  response: OrderType | null
  error: {
    status?: number
    message: string
  } | null
}

type OrdersListStateType = {
  isLoading: boolean
  response: OrdersListResponse | null
  error: {
    status?: number
    message: string
  } | null
}

type OrdersStoreState = {
  // Filters state
  filters: OrdersFilterParams
  setFilters: (filters: Partial<OrdersFilterParams>) => void
  resetFilters: () => void

  // List state
  ordersListState: OrdersListStateType
  fetchOrders: (params?: OrdersFilterParams) => Promise<OrdersListResponse>
  resetOrdersList: () => void

  // Single state
  orderState: OrderStateType
  getOrder: (id: string) => Promise<OrderType>
  deleteOrder: (id: string) => Promise<void>
  bulkDeleteOrders: (params: BulkIdParams) => Promise<BulkDeleteResponse>
  updateOrderStatus: (id: string, params: UpdateOrderStatusParams) => Promise<OrderType>
  resetOrder: () => void
}

const defaultFilters: OrdersFilterParams = {
  page: 1,
  size: 20
}

const initialOrderState: OrderStateType = {
  isLoading: false,
  response: null,
  error: null
}

const initialOrdersListState: OrdersListStateType = {
  isLoading: false,
  response: null,
  error: null
}

const useOrdersStore = create<OrdersStoreState>()(
  devtools((set, get) => ({
    filters: defaultFilters,
    orderState: initialOrderState,
    ordersListState: initialOrdersListState,

    setFilters: newFilters =>
      set(state => {
        const updatedFilters = { ...state.filters }
        Object.entries(newFilters).forEach(([key, value]) => {
          if (value === undefined) {
            delete updatedFilters[key as keyof OrdersFilterParams]
          } else {
            updatedFilters[key as keyof OrdersFilterParams] = value as any
          }
        })
        return { filters: updatedFilters }
      }),

    resetFilters: () => set({ filters: defaultFilters }),

    // List operations
    fetchOrders: async (params?: OrdersFilterParams) => {
      const currentState = get().ordersListState
      if (currentState.isLoading) {
        return currentState.response!
      }

      set(() => ({
        ordersListState: { ...initialOrdersListState, isLoading: true }
      }))

      try {
        const filtersToUse = params || get().filters
        const data = await fetchOrders(filtersToUse)
        set(() => ({ ordersListState: { ...initialOrdersListState, response: data } }))
        return data
      } catch (error: any) {
        set(() => ({
          ordersListState: {
            ...initialOrdersListState,
            error: { status: error.response?.status || 500, message: getErrorMessage(error) }
          }
        }))
        throw error
      }
    },

    resetOrdersList: () => set(() => ({ ordersListState: initialOrdersListState })),

    // Single operations
    getOrder: async (id: string) => {
      const currentState = get().orderState
      if (currentState.isLoading) {
        return currentState.response!
      }

      set(() => ({ orderState: { ...initialOrderState, isLoading: true } }))

      try {
        const data = await fetchOrder(id)
        set(() => ({ orderState: { ...initialOrderState, response: data } }))
        return data
      } catch (error: any) {
        set(() => ({
          orderState: {
            ...initialOrderState,
            error: { status: error.response?.status || 500, message: getErrorMessage(error) }
          }
        }))
        throw error
      }
    },

    updateOrderStatus: async (id: string, params: UpdateOrderStatusParams) => {
      set(state => ({ orderState: { ...state.orderState, isLoading: true, error: null } }))

      try {
        const data = await updateOrderStatus(id, params)
        set(() => ({ orderState: { ...initialOrderState, response: data } }))
        return data
      } catch (error: any) {
        set(state => ({
          orderState: {
            ...state.orderState,
            isLoading: false,
            error: { status: error.response?.status || 500, message: getErrorMessage(error) }
          }
        }))
        throw error
      }
    },

    resetOrder: () => set(() => ({ orderState: initialOrderState })),

    deleteOrder: async (id: string) => {
      await deleteOrder(id)
    },

    bulkDeleteOrders: async (params: BulkIdParams) => {
      return await bulkDeleteOrders(params)
    }
  }))
)

export default useOrdersStore
